import {
  FETCH_BOOKINGS,
  FETCH_BOOKINGS_SUCCESS,
  FETCH_BOOKINGS_FAILED,
  UPDATE_BOOKING,
  CANCEL_BOOKING
} from "../store/types";
import { fetchBookingLocations } from '../actions/locationactions';
import { RequestPushMsg } from '../other/NotificationFunctions';
import { sendWorkflowNotification } from '../other/NotificationFunctions';
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { addActualsToBooking, isFleetAdminPrivateClientBooking, saveAddresses, updateDriverQueue } from "../other/sharedFunctions";
import { GetDistance } from "../other/GeoFunctions";
import { get, onValue, update, off, remove, push, child, runTransaction } from "firebase/database";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import SHA256 from "crypto-js/sha256";
import { getBookingWorkflowForBookingRecord } from "../other/bookingWorkflow";
import { buildScopedBookingPatchUpdate } from "../other/bookingPrivacy";
import { callBookingTransaction } from "../other/bookingTransactionService";
import {
  EMPTY_CONTAINER_RETURN_PHOTO_REQUIREMENTS_VERSION,
  EMPTY_CONTAINER_RETURN_UPLOAD_REQUIREMENTS,
  validateEmptyContainerReturnSubmission
} from "../other/emptyContainerReturnProofs";

export { isFleetAdminPrivateClientBooking };

const updateScopedBookingPatch = async (bookingId, patch, options = {}) => {
  if (!bookingId || !patch) return;
  const { updates } = buildScopedBookingPatchUpdate(bookingId, patch, {
    ...options,
    includeAccess: false,
    includeIndexes: false
  });
  if (Object.keys(updates).length === 0) return;
  await update(firebase.rootRef, updates);
};

export const settleBookingPayment = (bookingId) => async () => {
  if (!bookingId) throw new Error("A booking ID is required for payment activation.");
  return callBookingTransaction("booking_settle_payment", { bookingId });
};

const DEFAULT_TERMINAL_GEOFENCE_RADIUS_KM = 0.5;

const normalizeProofLocation = (location) => {
  if (!location) {
    return { lat: null, lng: null };
  }
  return {
    lat: location.lat !== undefined ? location.lat : (location.latitude !== undefined ? location.latitude : null),
    lng: location.lng !== undefined ? location.lng : (location.longitude !== undefined ? location.longitude : null)
  };
};

const createProofHash = (proofPayload) => SHA256(JSON.stringify(proofPayload)).toString();

const getTerminalGeofenceProof = (location, target, settings) => {
  const proofLocation = normalizeProofLocation(location);
  const radiusKm = settings && settings.terminalGeofenceRadiusKm ? parseFloat(settings.terminalGeofenceRadiusKm) : DEFAULT_TERMINAL_GEOFENCE_RADIUS_KM;
  const canCalculate = proofLocation.lat !== null && proofLocation.lng !== null && target && target.lat !== undefined && target.lng !== undefined;
  const distanceKm = canCalculate ? GetDistance(proofLocation.lat, proofLocation.lng, target.lat, target.lng) : null;
  return {
    lat: proofLocation.lat,
    lng: proofLocation.lng,
    terminalLat: target && target.lat !== undefined ? target.lat : null,
    terminalLng: target && target.lng !== undefined ? target.lng : null,
    terminalGeofenceRadiusKm: radiusKm,
    terminalDistanceKm: distanceKm !== null ? parseFloat(distanceKm.toFixed(5)) : null,
    terminalGeofenceProof: distanceKm !== null ? distanceKm <= radiusKm : false
  };
};

const parseProofDate = (value) => {
  if (!value) {
    return new Date();
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? new Date() : date;
};

const buildProofRecord = ({ booking, proofType, imageType, imageUrl, location, target, settings, previousProofHash, capturedAt, stampText, stampEmbedded }) => {
  const now = parseProofDate(capturedAt);
  const geofenceProof = getTerminalGeofenceProof(location, target, settings);
  const payload = {
    bookingId: booking.id,
    bookingReference: booking.reference || null,
    proofType,
    imageType: imageType || null,
    imageUrl: imageUrl || null,
    timestamp: now.getTime(),
    timestampIso: now.toISOString(),
    ...geofenceProof,
    stampText: stampText || "",
    stampEmbedded: stampEmbedded === true,
    previousProofHash: previousProofHash || null
  };
  return {
    ...payload,
    hashAlgorithm: "SHA-256",
    blockchainFeature: "tamper-evident-proof-chain",
    proofHash: createProofHash(payload)
  };
};

const getLastProofHash = (booking) => {
  if (booking && booking.emptyPickUpContainer && booking.emptyPickUpContainer.gpsPictureProof && booking.emptyPickUpContainer.gpsPictureProof.proofHash) {
    return booking.emptyPickUpContainer.gpsPictureProof.proofHash;
  }
  if (booking && booking.emptyContainerReturns) {
    const returns = Object.values(booking.emptyContainerReturns);
    const lastReturn = returns
      .filter((item) => item && item.arrivalPictureProof && item.arrivalPictureProof.proofHash)
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))[0];
    if (lastReturn) {
      return lastReturn.arrivalPictureProof.proofHash;
    }
  }
  if (booking && booking.deliver_image_proof && booking.deliver_image_proof.proofHash) {
    return booking.deliver_image_proof.proofHash;
  }
  if (booking && booking.container_yard_image_proof && booking.container_yard_image_proof.proofHash) {
    return booking.container_yard_image_proof.proofHash;
  }
  if (booking && booking.pickup_image_proof && booking.pickup_image_proof.proofHash) {
    return booking.pickup_image_proof.proofHash;
  }
  if (booking && booking.arrivalProof && booking.arrivalProof.proofHash) {
    return booking.arrivalProof.proofHash;
  }
  return null;
};

const getBookingAddressText = (booking, pointKey, flatKey) => {
  const point = booking && booking[pointKey] ? booking[pointKey] : {};
  return point.add || point.description || point.address || (booking && booking[flatKey]) || "";
};

const getProfileName = (profile) => {
  if (!profile) return "";
  return [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim();
};

const getCompanyName = (profile) => {
  if (!profile) return "";
  return profile.companyName || profile.company_name || profile.businessName || profile.business_name || "";
};

const getFleetAdminDisplayName = (profile) => {
  if (!profile) return "Logistics Company";
  return (
    profile.companyName ||
    profile.company_name ||
    profile.businessName ||
    profile.business_name ||
    profile.fleetAdminName ||
    getProfileName(profile) ||
    profile.email ||
    "Logistics Company"
  );
};

const getBookingReference = (booking) => {
  return booking && booking.reference ? booking.reference : "this booking";
};

const formatNotificationAmount = (amount) => {
  const parsed = parseFloat(amount);
  return isNaN(parsed) ? amount : parsed.toFixed(2);
};

const sendBookingWorkflowNotification = async (payload) => {
  try {
    await sendWorkflowNotification(payload);
  } catch (error) {
    console.log(error);
  }
};

const claimPrivateBookingNotification = async (bookingId, eventKey) => {
  if (!bookingId || !eventKey) return false;
  let claimed = false;
  const markerRef = child(firebase.bookingPrivateRef(bookingId), `privateNotificationEvents/${eventKey}`);
  await runTransaction(markerRef, (current) => {
    if (current) return;
    claimed = true;
    return { sentAt: new Date().getTime() };
  });
  return claimed;
};

const getLinkedPrivateCustomer = async (booking) => {
  if (!booking || !isFleetAdminPrivateClientBooking(booking)) return null;
  const candidateUid = booking.customerUid || booking.clientId || booking.customer;
  if (!candidateUid || fieldMatchesUid(candidateUid, booking.fleetadmin) || fieldMatchesUid(candidateUid, booking.createdBy)) return null;
  const snapshot = await get(firebase.singleUserRef(candidateUid));
  if (!snapshot.exists()) return null;
  const customer = snapshot.val();
  return customer && customer.usertype === "customer" ? { uid: candidateUid, profile: customer } : null;
};

const notifyPrivateCustomerForTripEvent = async (booking, title, msg, eventKey) => {
  try {
    if (!isFleetAdminPrivateClientBooking(booking)) return;
    const linkedCustomer = await getLinkedPrivateCustomer(booking);
    if (!linkedCustomer) return;
    const claimed = await claimPrivateBookingNotification(booking.id, `customer_${eventKey}`);
    if (!claimed) return;
    await sendBookingWorkflowNotification({
      uid: linkedCustomer.uid,
      pushToken: linkedCustomer.profile.pushToken,
      title,
      msg,
      screen: "BookedCab",
      params: { bookingId: booking.id }
    });
  } catch (error) {
    console.log(error);
  }
};

const notifyFleetAdminForTripEvent = async (booking, title, msg, eventKey) => {
  try {
    if (!booking) return;
    const isPrivateBooking = isFleetAdminPrivateClientBooking(booking) && !!booking.fleetadmin;
    if (booking.acceptedOfferType !== "fleet" && !isPrivateBooking) return;
    const fleetAdminUid = isPrivateBooking ? booking.fleetadmin : (booking.acceptedFleetAdmin || booking.fleetadmin);
    if (!fleetAdminUid) return;
    if (isPrivateBooking) {
      const claimed = await claimPrivateBookingNotification(booking.id, `fleet_${eventKey || title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}`);
      if (!claimed) return;
    }
    const fleetAdminSnapshot = await get(firebase.singleUserRef(fleetAdminUid));
    const fleetAdmin = fleetAdminSnapshot.exists() ? fleetAdminSnapshot.val() : {};
    await sendBookingWorkflowNotification({
      uid: fleetAdminUid,
      pushToken: fleetAdmin.pushToken,
      title,
      msg,
      screen: "BookingHistory",
      params: { bookingId: booking.id }
    });
  } catch (error) {
    console.log(error);
  }
};

const validateFleetBidAmount = (amount) => {
  const parsedAmount = parseFloat(amount);
  if (isNaN(parsedAmount) || parsedAmount <= 0) {
    throw new Error("Bid amount must be greater than 0.");
  }
  return parsedAmount;
};

const parseAmount = (value) => {
  const amount = parseFloat(value);
  return isNaN(amount) ? 0 : amount;
};

const hasSettingValue = (value) => value !== undefined && value !== null && value !== "";

export const getFleetAdminWalletThreshold = (settings, amount) => {
  const type = settings && hasSettingValue(settings.fleetAdminWalletThresholdType)
    ? settings.fleetAdminWalletThresholdType
    : (settings && settings.driverWalletThresholdType ? settings.driverWalletThresholdType : "percentage");
  const defaultValue = type === "percentage" ? 20 : 0;
  const thresholdValue = settings && hasSettingValue(settings.fleetAdminWalletThresholdValue)
    ? parseAmount(settings.fleetAdminWalletThresholdValue)
    : (settings && hasSettingValue(settings.driverWalletThresholdValue)
    ? parseAmount(settings.driverWalletThresholdValue)
    : defaultValue);
  const minimumFlatRate = settings && hasSettingValue(settings.fleetAdminWalletMinimumFlatRate)
    ? settings.fleetAdminWalletMinimumFlatRate
    : (settings && settings.driverWalletMinimumFlatRate);
  const requiredBalance = type === "flat" ? thresholdValue : (parseAmount(amount) * thresholdValue / 100);
  return Math.max(requiredBalance, parseAmount(minimumFlatRate));
};

const hasFleetAdminValue = (value) => value !== undefined && value !== null && value !== "" && value !== false;

const isIndependentDriverProfile = (profile) => (
  profile &&
  profile.usertype === "driver" &&
  !hasFleetAdminValue(profile.fleetadmin)
);

const isActiveIndependentDriverOffer = (offer) => (
  offer &&
  offer.status !== "cancelled" &&
  (!hasFleetAdminValue(offer.fleetadmin) || offer.legacyPublicBid === true)
);

const normalizeAssignmentVehicleType = (value) => {
  const raw = typeof value === "string" || typeof value === "number"
    ? value
    : (value && (value.name || value.carType || value.vehicleType || value.vehicle_type || value.vehicleTypeName || value.carTypeName || value.requestedVehicleType || value.label)) || "";
  const normalized = `${raw}`
    .toLowerCase()
    .trim()
    .replace(/footer|foot/g, "ft")
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9]/g, "");
  if (!normalized) return "";
  if (/(20)(ft)/.test(normalized) || normalized.indexOf("20ft") >= 0) return "20ft";
  if (/(40)(ft)/.test(normalized) || normalized.indexOf("40ft") >= 0) return "40ft";
  if (/(10)(ft)/.test(normalized) || normalized.indexOf("10ft") >= 0) return "10ft";
  if (normalized.indexOf("l300") >= 0 || normalized.indexOf("fbvan") >= 0 || normalized.indexOf("vanaluminum") >= 0 || normalized === "fb" || normalized === "van") return "l300fbvan";
  if (normalized.indexOf("truck3tons") >= 0 || normalized.indexOf("3tons") >= 0 || normalized.indexOf("3ton") >= 0) return "truck3tons";
  if (normalized.indexOf("wingvan") >= 0) return "wingvan";
  return normalized;
};

const getAssignmentBookingVehicleKey = (booking) => normalizeAssignmentVehicleType(
  booking && (booking.carType || booking.vehicleType || booking.vehicle_type || booking.requestedVehicleType || booking.vehicleTypeName || booking.carDetails)
);

const getAssignmentCarVehicleKey = (car) => normalizeAssignmentVehicleType(
  car && (car.carType || car.vehicleType || car.vehicle_type || car.vehicleTypeName || car.carTypeName || car.name || car.cartype || car.type)
);

const isTruthyOrMissing = (value) => (
  value === undefined || value === null || value === "" || value === true || value === "true" || value === 1 || value === "1"
);

const isTruthyStatus = (value) => (
  value === true || value === "true" || value === 1 || value === "1"
);

const isDriverAccountEnabledForNotification = (driver) => !!(
  driver &&
  driver.usertype === "driver" &&
  isTruthyStatus(driver.active) &&
  isTruthyOrMissing(driver.approved) &&
  !isTruthyStatus(driver.blocked) &&
  !isTruthyStatus(driver.suspended) &&
  !isTruthyStatus(driver.deleted)
);

const shouldSendDriverBookingNotification = (driver) => (
  isDriverAccountEnabledForNotification(driver) &&
  isTruthyStatus(driver.driverActiveStatus)
);

const fieldMatchesUid = (fieldValue, uid) => {
  if (!fieldValue || !uid) return false;
  const normalizedUid = `${uid}`.toLowerCase().trim();
  if (Array.isArray(fieldValue)) {
    return fieldValue.some((value) => fieldMatchesUid(value, uid));
  }
  if (typeof fieldValue === "object") {
    return Object.keys(fieldValue).some((key) => fieldValue[key] && `${key}`.toLowerCase().trim() === normalizedUid);
  }
  return `${fieldValue}`.toLowerCase().trim() === normalizedUid;
};

const canProfileViewBooking = (booking, profile) => {
  if (!isFleetAdminPrivateClientBooking(booking)) return true;
  if (!profile) return false;
  if (profile.usertype === "admin") return true;
  if (profile.usertype === "fleetadmin") {
    return fieldMatchesUid(booking.fleetadmin, profile.uid) || fieldMatchesUid(booking.createdBy, profile.uid);
  }
  if (profile.usertype === "customer") {
    return fieldMatchesUid(booking.customerUid, profile.uid) || fieldMatchesUid(booking.customer, profile.uid);
  }
  if (profile.usertype === "driver") {
    return fieldMatchesUid(booking.assignedDriver, profile.uid) ||
      fieldMatchesUid(booking.driver, profile.uid) ||
      fieldMatchesUid(booking.acceptedDriver, profile.uid);
  }
  return false;
};

const privateFinancialFields = [
  "truckingChargeToClient",
  "superAdminCommissionType",
  "superAdminCommissionValue",
  "superAdminCommissionAmount",
  "additionalFees",
  "additionalFeesTotal",
  "totalAdditionalFeeAmount",
  "fleetAdminNetAmount",
  "fleetAdminWalletThresholdType",
  "fleetAdminWalletThresholdValue",
  "fleetAdminWalletMinimumFlatRate",
  "walletBalanceAtBooking",
  "walletThresholdRequiredAmount",
  "walletThresholdPassed",
  "driver_share",
  "fleetCommission",
  "fleet_admin_comission",
  "commission_type",
  "commission_rate",
  "convenience_fees"
];

const sanitizeBookingForProfile = (booking, profile) => {
  if (!booking || !profile || profile.usertype !== "customer" || !isFleetAdminPrivateClientBooking(booking)) {
    return booking;
  }
  const sanitizedBooking = { ...booking };
  privateFinancialFields.forEach((field) => {
    delete sanitizedBooking[field];
  });
  return sanitizedBooking;
};

const isAssignableFleetDriver = (driver, fleetAdminUid) => (
  !!(
    driver &&
    driver.usertype === "driver" &&
    fieldMatchesUid(driver.fleetadmin || driver.fleetadminUid || driver.createdBy, fleetAdminUid) &&
    isTruthyStatus(driver.active) &&
    isTruthyOrMissing(driver.approved) &&
    !isTruthyStatus(driver.blocked) &&
    !isTruthyStatus(driver.suspended) &&
    !isTruthyStatus(driver.deleted)
  )
);

const isAssignableFleetCar = (car, fleetAdminUid) => (
  !!(
    car &&
    fieldMatchesUid(car.fleetadmin || car.fleetAdmin || car.fleetadminId || car.fleetAdminId, fleetAdminUid) &&
    isTruthyOrMissing(car.active) &&
    isTruthyOrMissing(car.approved) &&
    isTruthyOrMissing(car.carApproved)
  )
);

const carHasDriverLink = (car) => (
  !!(car && (car.driver || car.driverId || car.driverUid))
);

const carLinkedToDriver = (car, driverUid) => (
  !carHasDriverLink(car) || fieldMatchesUid(car.driver || car.driverId || car.driverUid, driverUid)
);

const findAssignableFleetCar = (booking, driverUid, fleetAdminUid, cars) => {
  const bookingVehicleKey = getAssignmentBookingVehicleKey(booking);
  const carList = Array.isArray(cars) ? cars : (cars && typeof cars === "object" ? Object.values(cars) : []);
  if (!bookingVehicleKey || !driverUid || !fleetAdminUid) return null;
  return carList.find((car) => (
    isAssignableFleetCar(car, fleetAdminUid) &&
    carLinkedToDriver(car, driverUid) &&
    getAssignmentCarVehicleKey(car) === bookingVehicleKey
  )) || null;
};

const getDriverOfferAmount = (offer) => (
  offer && offer.amount !== undefined ? offer.amount : (
    offer && offer.trip_cost !== undefined ? offer.trip_cost : (
      offer && offer.bidPrice !== undefined ? offer.bidPrice : 0
    )
  )
);

const getDriverAcceptanceBreakdown = (booking, price) => {
  const tripCost = parseAmount(price || (booking && (booking.trip_cost || booking.estimate)));
  const convenienceFees = booking && booking.commission_type === "flat"
    ? parseAmount(booking.commission_rate)
    : (tripCost * parseAmount(booking && booking.commission_rate) / 100);
  const additionalFees = booking && booking.additionalFeesTotal !== undefined && booking.additionalFeesTotal !== null
    ? parseAmount(booking.additionalFeesTotal)
    : (booking && Array.isArray(booking.additionalFees)
      ? booking.additionalFees.reduce((total, fee) => total + parseAmount(fee.amount), 0)
      : 0);
  const total = convenienceFees + additionalFees;
  return {
    tripCost,
    convenienceFees,
    additionalFees,
    total,
    driverShare: Math.max(tripCost - total, 0)
  };
};

const getFleetBidBooking = async (bookingId) => {
  const snapshot = await get(firebase.singleBookingRef(bookingId));
  if (!snapshot.exists()) {
    throw new Error("Booking not found.");
  }
  const booking = snapshot.val();
  if (isFleetAdminPrivateClientBooking(booking)) {
    throw new Error("Private Fleet Admin client bookings are not available for public bidding.");
  }
  if (!booking.deliveryWithBid || booking.status !== "NEW") {
    throw new Error("Fleet bids can be changed only while the booking is open for bidding.");
  }
  return booking;
};

const getDriverBidBooking = async (bookingId) => {
  const snapshot = await get(firebase.singleBookingRef(bookingId));
  if (!snapshot.exists()) {
    throw new Error("Booking not found.");
  }
  const booking = snapshot.val();
  if (isFleetAdminPrivateClientBooking(booking)) {
    throw new Error("Private Fleet Admin client bookings are not available for public bidding.");
  }
  if (!booking.deliveryWithBid || booking.status !== "NEW" || booking.acceptedOfferType) {
    throw new Error("Driver bids can be changed only while the booking is open for bidding.");
  }
  return booking;
};

export const submitFleetBid = ({ bookingId, amount, note }) => async () => {
  const { auth, fleetOfferRef, settingsRef, singleUserRef } = firebase;
  const uid = auth.currentUser && auth.currentUser.uid;
  const profile = store.getState().auth.profile || {};
  if (!uid || profile.usertype !== "fleetadmin") {
    throw new Error("Only Fleet Admin can submit fleet bids.");
  }

  const bidAmount = validateFleetBidAmount(amount);
  const settingsSnapshot = await get(settingsRef);
  const settings = settingsSnapshot.exists() ? settingsSnapshot.val() : {};
  const fleetAdminSnapshot = await get(singleUserRef(uid));
  const fleetAdmin = fleetAdminSnapshot.exists() ? fleetAdminSnapshot.val() : profile;
  const requiredBalance = getFleetAdminWalletThreshold(settings, bidAmount);
  const walletBalance = parseAmount(fleetAdmin && fleetAdmin.walletBalance);
  if (walletBalance < requiredBalance) {
    throw new Error("Insufficient wallet balance. Please top up your Fleet Admin wallet to submit this bid.");
  }
  const booking = await getFleetBidBooking(bookingId);
  const existingOffer = booking.fleetOffers && booking.fleetOffers[uid] ? booking.fleetOffers[uid] : {};
  const now = new Date().getTime();
  const offer = {
    fleetAdminId: uid,
    fleetAdminName: getFleetAdminDisplayName(profile),
    companyName: getCompanyName(profile),
    amount: bidAmount,
    note: note ? note.toString().trim() : "",
    createdAt: existingOffer.createdAt || now,
    updatedAt: now,
    status: "submitted"
  };

  await update(fleetOfferRef(bookingId, uid), offer);
  const isBidUpdate = !!(existingOffer && existingOffer.status && existingOffer.status !== "cancelled");
  await sendBookingWorkflowNotification({
    uid: booking.customer,
    pushToken: booking.customer_token,
    title: isBidUpdate ? "Transport Company Bid Updated" : "New Transport Company Bid",
    msg: isBidUpdate
      ? `${getFleetAdminDisplayName(profile)} updated their bid for booking ${getBookingReference(booking)}.`
      : `${getFleetAdminDisplayName(profile)} submitted a bid of \u20b1${formatNotificationAmount(bidAmount)} for your booking ${getBookingReference(booking)}.`,
    screen: "BookedCab",
    params: { bookingId }
  });
  return offer;
};

export const submitDriverBid = () => async () => {
  throw new Error("Driver public bidding is disabled. Bookings are assigned by Logistics Company.");
};

export const cancelFleetBid = ({ bookingId }) => async () => {
  const { auth, fleetOfferRef } = firebase;
  const uid = auth.currentUser && auth.currentUser.uid;
  const profile = store.getState().auth.profile || {};
  if (!uid || profile.usertype !== "fleetadmin") {
    throw new Error("Only Fleet Admin can cancel fleet bids.");
  }

  const booking = await getFleetBidBooking(bookingId);
  const existingOffer = booking.fleetOffers && booking.fleetOffers[uid] ? booking.fleetOffers[uid] : null;
  if (!existingOffer || existingOffer.status === "cancelled") {
    throw new Error("No active fleet bid found.");
  }

  const now = new Date().getTime();
  const cancelledOffer = {
    ...existingOffer,
    fleetAdminId: uid,
    updatedAt: now,
    cancelledAt: now,
    status: "cancelled"
  };

  await update(fleetOfferRef(bookingId, uid), cancelledOffer);
  await sendBookingWorkflowNotification({
    uid: booking.customer,
    pushToken: booking.customer_token,
    title: "Transport Company Bid Cancelled",
    msg: `${getFleetAdminDisplayName(profile)} cancelled their bid for booking ${getBookingReference(booking)}.`,
    screen: "BookedCab",
    params: { bookingId }
  });
  return cancelledOffer;
};

export const cancelDriverBid = ({ bookingId }) => async () => {
  const { auth, driverOfferRef } = firebase;
  const uid = auth.currentUser && auth.currentUser.uid;
  const profile = store.getState().auth.profile || {};
  if (!uid || !isIndependentDriverProfile(profile)) {
    throw new Error("Only independent drivers can cancel public bids.");
  }

  const booking = await getDriverBidBooking(bookingId);
  const existingOffer = booking.driverOffers && booking.driverOffers[uid] ? booking.driverOffers[uid] : null;
  if (!existingOffer || existingOffer.status === "cancelled") {
    throw new Error("No active driver bid found.");
  }

  const now = new Date().getTime();
  const cancelledOffer = {
    ...existingOffer,
    driver: uid,
    driverId: uid,
    updatedAt: now,
    cancelledAt: now,
    status: "cancelled"
  };

  await update(driverOfferRef(bookingId, uid), cancelledOffer);
  await sendBookingWorkflowNotification({
    uid: booking.customer,
    pushToken: booking.customer_token,
    title: "Driver Bid Cancelled",
    msg: `${existingOffer.driver_name || existingOffer.driverName || "A driver"} cancelled their bid for booking ${getBookingReference(booking)}.`,
    screen: "BookedCab",
    params: { bookingId }
  });
  return cancelledOffer;
};

const getFleetOfferAmount = (offer) => (
  offer && offer.amount !== undefined ? offer.amount : (
    offer && offer.trip_cost !== undefined ? offer.trip_cost : 0
  )
);

const getFleetOfferDisplayName = (offer) => {
  return getFleetAdminDisplayName(offer);
};

export const acceptFleetOffer = ({ bookingId, fleetAdminUid }) => async () => {
  const { auth, singleBookingRef, singleUserRef } = firebase;
  const uid = auth.currentUser && auth.currentUser.uid;
  const profile = store.getState().auth.profile || {};
  if (!uid || profile.usertype !== "customer") {
    throw new Error("Only the customer can accept a fleet offer.");
  }

  const snapshot = await get(singleBookingRef(bookingId));
  if (!snapshot.exists()) {
    throw new Error("Booking not found.");
  }
  const booking = snapshot.val();
  if (isFleetAdminPrivateClientBooking(booking)) {
    throw new Error("Private Fleet Admin client bookings are not available for public bidding.");
  }
  if (booking.customer !== uid) {
    throw new Error("Only the booking customer can accept this offer.");
  }
  if (booking.status !== "NEW" || booking.acceptedOfferType) {
    throw new Error("This booking is no longer open for offer selection.");
  }
  const selectedOffer = booking.fleetOffers && booking.fleetOffers[fleetAdminUid] ? booking.fleetOffers[fleetAdminUid] : null;
  if (!selectedOffer || selectedOffer.status === "cancelled") {
    throw new Error("Fleet offer is not available.");
  }

  const amount = parseFloat(getFleetOfferAmount(selectedOffer));
  const acceptedFleetOffer = {
    fleetAdminId: fleetAdminUid,
    fleetAdminName: getFleetAdminDisplayName(selectedOffer),
    companyName: getCompanyName(selectedOffer),
    amount: isNaN(amount) ? 0 : amount,
    note: selectedOffer.note || "",
    createdAt: selectedOffer.createdAt || null,
    updatedAt: selectedOffer.updatedAt || null,
    status: selectedOffer.status || "submitted"
  };

  const updateData = {
    acceptedOfferType: "fleet",
    acceptedFleetAdmin: fleetAdminUid,
    acceptedFleetOffer,
    marketplaceStatus: "AWARDED",
    status: "AWAITING_DRIVER_ASSIGNMENT"
  };

  await updateScopedBookingPatch(bookingId, updateData, { includeIndexes: true });
  const fleetAdminSnapshot = await get(singleUserRef(fleetAdminUid));
  const fleetAdmin = fleetAdminSnapshot.exists() ? fleetAdminSnapshot.val() : {};
  await sendBookingWorkflowNotification({
    uid: fleetAdminUid,
    pushToken: fleetAdmin.pushToken,
    title: "Your Bid Was Accepted",
    msg: `Customer accepted your bid for booking ${getBookingReference(booking)}. Please assign a driver.`,
    screen: "BookingHistory",
    params: { bookingId }
  });
  return updateData;
};

export const acceptDriverOffer = ({ bookingId, driverUid }) => async () => {
  const { auth, singleBookingRef, singleUserRef } = firebase;
  const uid = auth.currentUser && auth.currentUser.uid;
  const profile = store.getState().auth.profile || {};
  if (!uid || profile.usertype !== "customer") {
    throw new Error("Only the customer can accept a driver offer.");
  }

  const snapshot = await get(singleBookingRef(bookingId));
  if (!snapshot.exists()) {
    throw new Error("Booking not found.");
  }
  const booking = snapshot.val();
  if (isFleetAdminPrivateClientBooking(booking)) {
    throw new Error("Private Fleet Admin client bookings are not available for public bidding.");
  }
  if (booking.customer !== uid) {
    throw new Error("Only the booking customer can accept this offer.");
  }
  if (booking.status !== "NEW" || booking.acceptedOfferType) {
    throw new Error("This booking is no longer open for offer selection.");
  }

  const selectedOffer = booking.driverOffers && booking.driverOffers[driverUid] ? booking.driverOffers[driverUid] : null;
  if (!isActiveIndependentDriverOffer(selectedOffer)) {
    throw new Error("Driver offer is not available.");
  }

  const driverSnapshot = await get(singleUserRef(driverUid));
  const driver = driverSnapshot.exists() ? driverSnapshot.val() : {};
  if (hasFleetAdminValue(driver.fleetadmin) && selectedOffer.legacyPublicBid !== true) {
    throw new Error("Fleet-linked driver offers must be handled by the Fleet Admin workflow.");
  }

  const amount = parseFloat(getDriverOfferAmount(selectedOffer));
  const acceptedDriverOffer = {
    ...selectedOffer,
    driver: driverUid,
    driverId: driverUid,
    amount: isNaN(amount) ? 0 : amount,
    trip_cost: isNaN(amount) ? "0.00" : amount.toFixed(2),
    status: selectedOffer.status || "submitted"
  };
  const filteredDriverOffers = {
    [driverUid]: acceptedDriverOffer
  };
  const requestedDrivers = {};
  if (booking.requestedDrivers && booking.requestedDrivers[driverUid]) {
    requestedDrivers[driverUid] = booking.requestedDrivers[driverUid];
  }

  const updateData = {
    acceptedOfferType: "driver",
    acceptedDriver: driverUid,
    acceptedDriverOffer,
    selectedBid: acceptedDriverOffer,
    marketplaceStatus: "AWARDED",
    driverOffers: filteredDriverOffers,
    requestedDrivers,
    driver: driverUid,
    driver_name: acceptedDriverOffer.driver_name || acceptedDriverOffer.driverName || getProfileName(driver),
    driverName: acceptedDriverOffer.driverName || acceptedDriverOffer.driver_name || getProfileName(driver),
    driver_image: acceptedDriverOffer.driver_image || driver.profile_image || "",
    driver_contact: acceptedDriverOffer.driver_contact || driver.mobile || "",
    driver_phone: acceptedDriverOffer.driver_contact || driver.mobile || "",
    driver_email: acceptedDriverOffer.driver_email || driver.email || "",
    driver_token: acceptedDriverOffer.driver_token || driver.pushToken || "",
    vehicle_number: acceptedDriverOffer.vehicle_number || driver.vehicleNumber || "",
    vehicleModel: acceptedDriverOffer.vehicleModel || driver.vehicleModel || "",
    vehicleMake: acceptedDriverOffer.vehicleMake || driver.vehicleMake || "",
    driverVehicleType: acceptedDriverOffer.driverVehicleType || driver.carType || "",
    driverRating: acceptedDriverOffer.driverRating || driver.rating || "0",
    trip_cost: acceptedDriverOffer.trip_cost,
    convenience_fees: acceptedDriverOffer.convenience_fees || booking.convenience_fees || "0.00",
    driver_share: acceptedDriverOffer.driver_share || booking.driver_share || "0.00",
    driverAcceptanceFeeTotal: acceptedDriverOffer.driverAcceptanceFeeTotal || booking.driverAcceptanceFeeTotal || "0.00",
    driverAcceptanceFeeBreakdown: acceptedDriverOffer.driverAcceptanceFeeBreakdown || booking.driverAcceptanceFeeBreakdown || null,
    fleetadmin: null,
    fleetadminName: null
  };

  await updateScopedBookingPatch(bookingId, updateData, { includeIndexes: true });
  await sendBookingWorkflowNotification({
    uid: driverUid,
    pushToken: acceptedDriverOffer.driver_token || driver.pushToken,
    title: "Your Bid Was Accepted",
    msg: `Customer accepted your bid for booking ${getBookingReference(booking)}.`,
    screen: "BookedCab",
    params: { bookingId }
  });
  return updateData;
};

export const assignFleetBidDriver = ({ bookingId, driverUid }) => async (dispatch) => {
  const { auth, carsRef, singleBookingRef, singleUserRef } = firebase;
  const uid = auth.currentUser && auth.currentUser.uid;
  const profile = store.getState().auth.profile || {};
  if (!uid || profile.usertype !== "fleetadmin") {
    throw new Error("Only the winning Fleet Admin can assign a driver.");
  }

  const bookingSnapshot = await get(singleBookingRef(bookingId));
  if (!bookingSnapshot.exists()) {
    throw new Error("Booking not found.");
  }
  const booking = bookingSnapshot.val();
  const isWinningFleetAdmin = booking.acceptedFleetAdmin === uid || booking.fleetadmin === uid;
  if (booking.status !== "AWAITING_DRIVER_ASSIGNMENT" || booking.acceptedOfferType !== "fleet" || !isWinningFleetAdmin) {
    throw new Error("This booking is not available for your fleet driver assignment.");
  }

  const driverSnapshot = await get(singleUserRef(driverUid));
  if (!driverSnapshot.exists()) {
    throw new Error("Driver not found.");
  }
  const driver = driverSnapshot.val();
  const driverFleetAdmin = driver && (driver.fleetadmin || driver.fleetadminUid || driver.createdBy);
  const isOwnedFleetDriver = driver &&
    driver.usertype === "driver" &&
    fieldMatchesUid(driverFleetAdmin, uid) &&
    isTruthyStatus(driver.active) &&
    isTruthyOrMissing(driver.approved) &&
    !isTruthyStatus(driver.blocked) &&
    !isTruthyStatus(driver.suspended) &&
    !isTruthyStatus(driver.deleted);
  if (!isOwnedFleetDriver) {
    throw new Error("You can assign only drivers under your fleet.");
  }
  const carsSnapshot = await get(carsRef(uid, "fleetadmin"));
  const fleetCars = carsSnapshot.exists() ? carsSnapshot.val() : {};
  const assignedCar = findAssignableFleetCar(booking, driverUid, uid, fleetCars);
  if (!assignedCar) {
    throw new Error("Selected driver/vehicle does not match the booking vehicle type.");
  }

  const driverName = `${driver.firstName || ""} ${driver.lastName || ""}`.trim();
  const requestedDrivers = {
    ...(booking.requestedDrivers || {}),
    [driverUid]: true
  };
  const updateData = {
    driver: driverUid,
    driver_name: driverName,
    driverName,
    driver_image: driver.profile_image || "",
    driver_contact: driver.mobile || "",
    driver_phone: driver.mobile || "",
    driver_token: driver.pushToken || "",
    vehicle_number: assignedCar.vehicleNumber || assignedCar.vehicle_number || driver.vehicleNumber || "",
    vehicleModel: assignedCar.vehicleModel || driver.vehicleModel || "",
    vehicleMake: assignedCar.vehicleMake || driver.vehicleMake || "",
    driverVehicleType: assignedCar.carType || assignedCar.vehicleType || assignedCar.vehicle_type || driver.carType || "",
    requestedDrivers,
    assignedByFleetAdmin: uid,
    assignedAt: new Date().getTime(),
    status: "ACCEPTED"
  };

  await updateScopedBookingPatch(bookingId, updateData, { includeIndexes: true });
  const updatedBooking = {
    ...booking,
    id: bookingId,
    ...updateData
  };
  dispatch({
    type: UPDATE_BOOKING,
    payload: updatedBooking
  });
  if (shouldSendDriverBookingNotification(driver)) {
    await sendBookingWorkflowNotification({
      uid: driverUid,
      pushToken: driver.pushToken,
      title: "New Assigned Booking",
      msg: `You have been assigned booking ${getBookingReference(updatedBooking)}. Please check your driver app.`,
      screen: "DriverTrips",
      params: { bookingId }
    });
  }
  await sendBookingWorkflowNotification({
    uid: booking.customer,
    pushToken: booking.customer_token,
    title: "Driver Assigned",
    msg: `${driverName || "A driver"} has been assigned to your booking ${getBookingReference(updatedBooking)}.`,
    screen: "BookedCab",
    params: { bookingId }
  });
  return updatedBooking;
};

export const assignFleetPrivateClientDriver = ({ bookingId, driverUid, vehicleId }) => async (dispatch) => {
  const { auth, carsRef, singleBookingRef, singleUserRef } = firebase;
  const uid = auth.currentUser && auth.currentUser.uid;
  const profile = store.getState().auth.profile || {};
  if (!uid || profile.usertype !== "fleetadmin") {
    throw new Error("Only the owner Fleet Admin can assign this private booking.");
  }
  if (!bookingId || !driverUid || !vehicleId) {
    throw new Error("Select both a driver and vehicle.");
  }

  const bookingSnapshot = await get(singleBookingRef(bookingId));
  if (!bookingSnapshot.exists()) {
    throw new Error("Booking not found.");
  }
  const booking = bookingSnapshot.val();
  const isOwner = fieldMatchesUid(booking.fleetadmin, uid) || fieldMatchesUid(booking.createdBy, uid);
  if (
    booking.bookingSource !== "fleet_admin_client" ||
    booking.visibility !== "private" ||
    !isOwner ||
    (booking.status !== "NEW" && booking.status !== "AWAITING_DRIVER_ASSIGNMENT")
  ) {
    throw new Error("This private client booking is not available for your assignment.");
  }

  const driverSnapshot = await get(singleUserRef(driverUid));
  if (!driverSnapshot.exists()) {
    throw new Error("Driver not found.");
  }
  const driver = driverSnapshot.val();
  if (!isAssignableFleetDriver(driver, uid)) {
    throw new Error("You can assign only drivers under your fleet.");
  }

  const carsSnapshot = await get(carsRef(uid, "fleetadmin"));
  const fleetCars = carsSnapshot.exists() ? carsSnapshot.val() : {};
  const assignedCar = Array.isArray(fleetCars)
    ? fleetCars.find((car) => car && `${car.id}` === `${vehicleId}`)
    : fleetCars[vehicleId];
  const vehicleMatchesBooking = assignedCar && getAssignmentCarVehicleKey(assignedCar) === getAssignmentBookingVehicleKey(booking);
  if (
    !assignedCar ||
    !isAssignableFleetCar(assignedCar, uid) ||
    !carLinkedToDriver(assignedCar, driverUid) ||
    !vehicleMatchesBooking
  ) {
    throw new Error("Selected vehicle must belong to your fleet, match the driver, and support the booking vehicle type.");
  }

  const assignedAt = new Date().getTime();
  const driverName = `${driver.firstName || ""} ${driver.lastName || ""}`.trim();
  const rawVehicleName = assignedCar.name || assignedCar.carType || assignedCar.vehicleType || assignedCar.vehicleModel || "";
  const vehicleName = typeof rawVehicleName === "string"
    ? rawVehicleName
    : (rawVehicleName && rawVehicleName.name ? rawVehicleName.name : "");
  const vehiclePlate = assignedCar.vehicleNumber || assignedCar.vehicle_number || assignedCar.plateNumber || "";
  const requestedDrivers = {
    ...(booking.requestedDrivers || {}),
    [driverUid]: true
  };
  const updateData = {
    assignedDriver: driverUid,
    assignedDriverName: driverName,
    assignedDriverContact: driver.mobile || "",
    assignedVehicle: vehicleId,
    assignedVehicleName: vehicleName,
    assignedVehiclePlate: vehiclePlate,
    assignedBy: uid,
    assignedByRole: "fleetadmin",
    assignedAt,
    driver: driverUid,
    driver_name: driverName,
    driverName,
    driver_image: driver.profile_image || "",
    driver_contact: driver.mobile || "",
    driver_phone: driver.mobile || "",
    driver_token: driver.pushToken || "",
    vehicle_number: vehiclePlate,
    vehicleModel: assignedCar.vehicleModel || driver.vehicleModel || "",
    vehicleMake: assignedCar.vehicleMake || driver.vehicleMake || "",
    driverVehicleType: assignedCar.carType || assignedCar.vehicleType || assignedCar.vehicle_type || driver.carType || "",
    requestedDrivers,
    status: "ACCEPTED"
  };

  await updateScopedBookingPatch(bookingId, updateData, { includeIndexes: true });
  const updatedBooking = {
    ...booking,
    id: bookingId,
    ...updateData
  };
  dispatch({
    type: UPDATE_BOOKING,
    payload: updatedBooking
  });
  const notificationClaimed = await claimPrivateBookingNotification(bookingId, "driverAssigned");
  if (notificationClaimed) {
    const pickup = getBookingAddressText(updatedBooking, "pickup", "pickupAddress");
    const delivery = getBookingAddressText(updatedBooking, "drop", "dropAddress");
    const vehicleDisplay = [vehicleName, vehiclePlate].filter(Boolean).join(" - ");
    const clientName = updatedBooking.clientName || updatedBooking.customer_name || "private client";
    if (shouldSendDriverBookingNotification(driver)) {
      await sendBookingWorkflowNotification({
        uid: driverUid,
        pushToken: driver.pushToken,
        title: "New Private Trip Assigned",
        msg: `You have been assigned booking ${getBookingReference(updatedBooking)} from ${pickup} to ${delivery}. ` +
          `Vehicle: ${vehicleDisplay || "assigned fleet vehicle"}. Client contact: ${updatedBooking.clientContact || "available in trip details"}.`,
        screen: "DriverTrips",
        params: { bookingId }
      });
    }
    await sendBookingWorkflowNotification({
      uid,
      pushToken: profile.pushToken,
      title: "Private Booking Driver Assigned",
      msg: `${driverName || "Driver"} and ${vehicleDisplay || "a fleet vehicle"} were assigned to booking ` +
        `${getBookingReference(updatedBooking)} for ${clientName}.`,
      screen: "BookingHistory",
      params: { bookingId }
    });
    const linkedCustomer = await getLinkedPrivateCustomer(updatedBooking);
    if (linkedCustomer) {
      await sendBookingWorkflowNotification({
        uid: linkedCustomer.uid,
        pushToken: linkedCustomer.profile.pushToken,
        title: "Driver Assigned",
        msg: `${driverName || "A driver"} has been assigned to booking ${getBookingReference(updatedBooking)} ` +
          `from ${pickup} to ${delivery}.`,
        screen: "BookedCab",
        params: { bookingId }
      });
    }
  }
  return updatedBooking;
};


const mergeScopedBookingForProfile = async (bookingId, profile) => {
  const {
    singleBookingRef,
    bookingOperationsRef,
    bookingPrivateRef,
    bookingClientChargesRef,
    bookingFinancialsRef
  } = firebase;
  const coreSnap = await get(singleBookingRef(bookingId));
  if (!coreSnap.exists()) return null;
  const core = coreSnap.val() || {};
  const scopedReads = [get(bookingOperationsRef(bookingId))];
  const role = profile && profile.usertype;
  if (role === "customer") {
    scopedReads.push(get(bookingPrivateRef(bookingId)));
    scopedReads.push(get(bookingClientChargesRef(bookingId)));
  } else if (role === "fleetadmin" || role === "admin" || role === "superadmin" || role === "super_admin" || role === "alladmins" || role === "all_admins") {
    scopedReads.push(get(bookingPrivateRef(bookingId)));
    scopedReads.push(get(bookingClientChargesRef(bookingId)));
    scopedReads.push(get(bookingFinancialsRef(bookingId)));
  }
  const snapshots = await Promise.all(scopedReads);
  return Object.assign(
    {},
    core,
    ...snapshots.map((snapshot) => snapshot.exists() ? snapshot.val() : {}),
    { id: bookingId }
  );
};

const loadIndexedBookingsForProfile = async (indexData, profile) => {
  const ids = Object.keys(indexData || {}).filter((key) => indexData[key] === true || indexData[key]);
  const bookings = [];
  for (let i = 0; i < ids.length; i++) {
    const booking = await mergeScopedBookingForProfile(ids[i], profile);
    if (booking) bookings.push(booking);
  }
  return bookings;
};

const BOOKING_LOAD_FAILED_MESSAGE = "Unable to load booking data.";

const getBookingLoadErrorMessage = () => {
  const language = store.getState().languagedata && store.getState().languagedata.defaultLanguage;
  return language && language.unable_to_load_booking_data
    ? language.unable_to_load_booking_data
    : BOOKING_LOAD_FAILED_MESSAGE;
};

const dispatchBookingLoadFailure = (dispatch) => {
  dispatch({
    type: FETCH_BOOKINGS_FAILED,
    payload: getBookingLoadErrorMessage(),
  });
};

export const fetchBookings = () => (dispatch) => {

  const {
    bookingListRef,
  } = firebase;

  dispatch({
    type: FETCH_BOOKINGS,
    payload: null,
  });

  const userInfo = store.getState().auth.profile;
  const listRef = bookingListRef(userInfo.uid, userInfo.usertype);

  off(listRef);
  let listenerFailureDispatched = false;
  const failBookingLoad = () => {
    if (listenerFailureDispatched) return;
    listenerFailureDispatched = true;
    dispatchBookingLoadFailure(dispatch);
  };

  onValue(listRef, async (snapshot) => {
    try {
      if (snapshot.val()) {
        const rawData = snapshot.val();
        const data = await loadIndexedBookingsForProfile(rawData, userInfo);
        const active = [];
        let tracked = null;
        const bookings = data
          .map((booking) => {
            booking.pickupAddress = getBookingAddressText(booking, "pickup", "pickupAddress");
            booking.dropAddress = getBookingAddressText(booking, "drop", "dropAddress");
            booking.discount = booking.discount ? booking.discount : 0;
            booking.cashPaymentAmount = booking.cashPaymentAmount ? booking.cashPaymentAmount : 0;
            booking.cardPaymentAmount = booking.cardPaymentAmount ? booking.cardPaymentAmount : 0;
            return sanitizeBookingForProfile(booking, userInfo);
          })
          .filter((booking) => canProfileViewBooking(booking, userInfo));
        for (let i = 0; i < bookings.length; i++) {
          if (['PAYMENT_PENDING','NEW', 'AWAITING_DRIVER_ASSIGNMENT', 'ACCEPTED', 'ARRIVED', 'STARTED', 'REACHED', 'PENDING', 'PAID'].indexOf(bookings[i].status) != -1) {
            active.push(bookings[i]);
          }
          if ((['ACCEPTED', 'ARRIVED', 'STARTED'].indexOf(bookings[i].status) != -1) && userInfo.usertype == 'driver') {
            tracked = bookings[i];
            fetchBookingLocations(tracked.id)(dispatch);
          }
        }
        dispatch({
          type: FETCH_BOOKINGS_SUCCESS,
          payload: {
            bookings: bookings.reverse(),
            active: active,
            tracked: tracked
          },
        });
        if (tracked) {
          dispatch({
            type: FETCH_BOOKINGS_SUCCESS,
            payload: null
          });
        }
      } else {
        dispatch({
          type: FETCH_BOOKINGS_FAILED,
          payload: store.getState().languagedata.defaultLanguage.no_bookings,
        });
      }
    } catch (error) {
      failBookingLoad();
    }
  }, () => {
    failBookingLoad();
  });
};

export const updateBooking = (booking) => async (dispatch) => {

  const {
    auth,
    trackingRef,
    singleBookingRef,
    singleUserRef,
    walletHistoryRef,
    settingsRef,
    userRatingsRef
  } = firebase;

  dispatch({
    type: UPDATE_BOOKING,
    payload: booking,
  });

  const settingsdata = await get(settingsRef);
  const settings = settingsdata.val();
  
  if (booking.status == 'PAYMENT_PENDING') {
    updateScopedBookingPatch(booking.id, booking);
  }
  if (booking.status == 'NEW' || booking.status == 'ACCEPTED') {
    updateScopedBookingPatch(booking.id, updateDriverQueue(booking));
  }
  if (booking.status == 'ARRIVED') {
    let dt = new Date();
    const driverLocation = store.getState().gpsdata.location;
    const arrivalProof = buildProofRecord({
      booking,
      proofType: "arrival",
      location: driverLocation,
      target: booking.pickup,
      settings,
      previousProofHash: getLastProofHash(booking)
    });
    booking.driver_arrive_time = dt.getTime().toString();
    booking.arrivalProof = arrivalProof;
    booking.arrivalTime = arrivalProof.timestamp;
    booking.arrivalTimestamp = arrivalProof.timestampIso;
    booking.arrivalLat = arrivalProof.lat;
    booking.arrivalLng = arrivalProof.lng;
    booking.arrivalTerminalGeofenceProof = arrivalProof.terminalGeofenceProof;
    booking.arrivalProofHash = arrivalProof.proofHash;
    updateScopedBookingPatch(booking.id, booking);
    if(booking.customer_token && !isFleetAdminPrivateClientBooking(booking)){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.driver_near,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
    }
    await notifyPrivateCustomerForTripEvent(
      booking,
      "Driver Arrived",
      `The driver has arrived for booking ${getBookingReference(booking)}.`,
      "arrived"
    );
    if (isFleetAdminPrivateClientBooking(booking)) {
      await notifyFleetAdminForTripEvent(
        booking,
        "Driver Arrived",
        `Driver has arrived for booking ${getBookingReference(booking)} for ${booking.clientName || "the private client"}.`,
        "arrived"
      );
    }
  }
  
  if (booking.status == 'STARTED') {
    let dt = new Date();
    let localString = dt.getHours() + ":" + dt.getMinutes() + ":" + dt.getSeconds();
    let timeString = dt.getTime();
    booking.trip_start_time = localString;
    booking.startTime = timeString;
    updateScopedBookingPatch(booking.id, booking);

    const driverLocation = store.getState().gpsdata.location;
    
    push(trackingRef(booking.id),{
      at: new Date().getTime(),
      status: 'STARTED',
      lat: driverLocation.lat,
      lng: driverLocation.lng
    });

    if(booking.customer_token && !isFleetAdminPrivateClientBooking(booking)){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.driver_journey_msg + booking.reference,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
      }
    await notifyPrivateCustomerForTripEvent(
      booking,
      "Trip Started",
      `Booking ${getBookingReference(booking)} is now in progress.`,
      "started"
    );
    await notifyFleetAdminForTripEvent(
      booking,
      "Trip Started",
      isFleetAdminPrivateClientBooking(booking)
        ? `Driver has started booking ${getBookingReference(booking)} for ${booking.clientName || "the private client"}.`
        : `Driver has started booking ${getBookingReference(booking)}.`,
      "started"
    );
   }

  if (booking.status == 'REACHED') {

    const driverLocation = store.getState().gpsdata.location;

    push(trackingRef(booking.id),{
      at: new Date().getTime(),
      status: 'REACHED',
      lat: driverLocation.lat,
      lng: driverLocation.lng
    });

    let address = await saveAddresses(booking,driverLocation);

    let bookingObj = await addActualsToBooking(booking, address, driverLocation);
    updateScopedBookingPatch(booking.id, bookingObj);

    if(booking.customer_token && !isFleetAdminPrivateClientBooking(booking)){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.driver_completed_ride,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
    }
    await notifyPrivateCustomerForTripEvent(
      booking,
      "Trip Reached Destination",
      `Booking ${getBookingReference(booking)} has reached its destination.`,
      "reached"
    );
    await notifyFleetAdminForTripEvent(
      booking,
      "Trip Completed",
      isFleetAdminPrivateClientBooking(booking)
        ? `Booking ${getBookingReference(booking)} for ${booking.clientName || "the private client"} has reached its destination.`
        : `Booking ${getBookingReference(booking)} has been completed.`,
      "reached"
    );
  }

  if (booking.status == 'PENDING') {
    updateScopedBookingPatch(booking.id, booking);
    update(singleUserRef(booking.driver), { queue: false });
  }
  if (booking.status == 'PAID') {
    if(booking.booking_from_web){
      booking.status = 'COMPLETE';
    }
    updateScopedBookingPatch(booking.id, booking);
    if(booking.driver == auth.currentUser.uid && (booking.prepaid || booking.payment_mode == 'cash' || booking.payment_mode == 'wallet')){
      update(singleUserRef(booking.driver), { queue: false });
    }

    if(booking.customer_token && !isFleetAdminPrivateClientBooking(booking)){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.success_payment,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
    }

    if(booking.driver_token){
        RequestPushMsg(
          booking.driver_token,
          {
              title: store.getState().languagedata.defaultLanguage.notification_title,
              msg: store.getState().languagedata.defaultLanguage.success_payment,
              screen: 'BookedCab',
              params: { bookingId: booking.id }
          });
      }
    if (isFleetAdminPrivateClientBooking(booking)) {
      await notifyPrivateCustomerForTripEvent(
        booking,
        "Booking Payment Updated",
        `Payment status was updated for booking ${getBookingReference(booking)}.`,
        "paid"
      );
      await notifyFleetAdminForTripEvent(
        booking,
        "Private Booking Payment Updated",
        `Payment status was updated for booking ${getBookingReference(booking)} for ${booking.clientName || "the private client"}.`,
        "paid"
      );
    }
    }

  if (booking.status == 'COMPLETE') {
    updateScopedBookingPatch(booking.id, booking);
    if (isFleetAdminPrivateClientBooking(booking)) {
      await notifyPrivateCustomerForTripEvent(
        booking,
        "Booking Completed",
        `Booking ${getBookingReference(booking)} has been completed.`,
        "completed"
      );
      await notifyFleetAdminForTripEvent(
        booking,
        "Private Booking Completed",
        `Booking ${getBookingReference(booking)} for ${booking.clientName || "the private client"} has been completed by ${booking.assignedDriverName || booking.driver_name || "the assigned driver"}.`,
        "completed"
      );
    }
    if (booking.rating) {
      if(booking.driver_token){
        RequestPushMsg(
          booking.driver_token,
          {
              title: store.getState().languagedata.defaultLanguage.notification_title,
              msg:  store.getState().languagedata.defaultLanguage.received_rating.toString().replace("X", booking.rating.toString()),
              screen: 'BookedCab',
              params: { bookingId: booking.id }
          });
      }
      onValue(userRatingsRef(booking.driver), snapshot => {
        let ratings = snapshot.val();
        let rating;
        if(ratings){
          let sum = 0;
          const arr = Object.values(ratings);
          for (let i = 0; i< arr.length ; i++){
            sum = sum + arr[i].rate
          }
          sum = sum + booking.rating;
          rating = parseFloat(sum / (arr.length + 1)).toFixed(1);
        }else{
          rating =  booking.rating;
        }
        update(singleUserRef(booking.driver),{rating: rating});
        push(userRatingsRef(booking.driver), {
          user: booking.customer,
          rate: booking.rating,
          bookingId: booking.id
        });
      }, { onlyOnce: true});
    }
  }
};

export const cancelBooking = (data) => (dispatch) => {
  const {
    singleBookingRef,
    singleUserRef,
    requestedDriversRef
  } = firebase;

  dispatch({
    type: CANCEL_BOOKING,
    payload: data,
  });

  updateScopedBookingPatch(data.booking.id,{
    status: 'CANCELLED',
    reason: data.reason,
    cancelledBy: data.cancelledBy
  }).then(() => {
    if (data.booking.driver && (data.booking.status === 'NEW' || data.booking.status === 'ACCEPTED' || data.booking.status === 'ARRIVED')) {
      update(singleUserRef(data.booking.driver),{ queue: false });
      if(data.booking.driver_token){
        RequestPushMsg(
          data.booking.driver_token,
          {
              title: store.getState().languagedata.defaultLanguage.notification_title,
              msg:  store.getState().languagedata.defaultLanguage.booking_cancelled + data.booking.id,
              screen: 'BookedCab',
              params: { bookingId: data.booking.id }
          });
        }

        if(data.booking.customer_token){
          RequestPushMsg(
            data.booking.customer_token,
            {
                title: store.getState().languagedata.defaultLanguage.notification_title,
                msg:  store.getState().languagedata.defaultLanguage.booking_cancelled + data.booking.id,
                screen: 'BookedCab',
                params: { bookingId: data.booking.id }
            });
        }
   }
    if (data.booking.status === 'NEW') {
      remove(requestedDriversRef(data.booking.id));
    }
  });
};

const canUseEmptyContainerReturnWorkflow = (booking) => {
  const workflowConfigs = store.getState().workflowconfigs && store.getState().workflowconfigs.configs ? store.getState().workflowconfigs.configs : [];
  return !!getBookingWorkflowForBookingRecord(booking, workflowConfigs).allowEmptyReturn;
};

const getEmptyReturnConsigneeName = (booking) => {
  if (!booking) {
    return "";
  }
  return booking.importBookingInfo && booking.importBookingInfo.consigneeName
    ? booking.importBookingInfo.consigneeName
    : booking.consigneeName || booking.customer_name || "";
};

const hasExportBookingInfo = (booking) => {
  if (!booking || !booking.exportBookingInfo) {
    return false;
  }
  const info = booking.exportBookingInfo;
  return !!(info.exporterShipperName || info.portOfLoading || info.containerNumber || info.bookingNumber || info.shippingLine);
};

const canUseEmptyPickUpContainerWorkflow = (booking) => {
  if (!booking || booking.emptyPickUpContainer) {
    return false;
  }
  const workflowConfigs = store.getState().workflowconfigs && store.getState().workflowconfigs.configs ? store.getState().workflowconfigs.configs : [];
  return !!getBookingWorkflowForBookingRecord(booking, workflowConfigs).allowEmptyPickup && hasExportBookingInfo(booking);
};

const getBookingWorkflowFromStore = (booking) => {
  const workflowConfigs = store.getState().workflowconfigs && store.getState().workflowconfigs.configs ? store.getState().workflowconfigs.configs : [];
  return getBookingWorkflowForBookingRecord(booking, workflowConfigs);
};

const shouldSendWorkflowEmail = (booking, triggerKey) => {
  const workflow = getBookingWorkflowFromStore(booking);
  const enabled = workflow.emailTriggers && workflow.emailTriggers[triggerKey] !== false;
  console.log("Booking workflow email trigger resolved", {
    bookingId: booking && (booking.id || booking.key),
    trigger: triggerKey,
    enabled,
    workflowSource: workflow.workflowSource || "fallback",
    workflowConfigId: workflow.workflowConfigId || ""
  });
  return enabled;
};

const getEmptyPickUpExporterShipperName = (booking) => {
  if (!booking) {
    return "";
  }
  return booking.exportBookingInfo && booking.exportBookingInfo.exporterShipperName
    ? booking.exportBookingInfo.exporterShipperName
    : booking.exporterShipperName || "";
};

const getEmptyPickUpContainerNumbers = (booking) => {
  const numbers = [];
  const add = (value) => {
    if (value && numbers.indexOf(value.toString().trim()) === -1) {
      numbers.push(value.toString().trim());
    }
  };
  add(booking && booking.exportBookingInfo && booking.exportBookingInfo.containerNumber);
  add(booking && booking.containerNumber);
  return numbers;
};

export const submitEmptyPickUpContainer = (booking, pickupData, imageBlob) => async (dispatch) => {
  const {
    singleBookingRef,
    bookingImageRef,
    config
  } = firebase;

  if (!booking || !booking.id) {
    throw new Error("Missing booking.");
  }
  if (!canUseEmptyPickUpContainerWorkflow(booking)) {
    throw new Error("Empty Pick Up Container is available only for unsubmitted 20ft/40ft Export logistics bookings.");
  }
  const currentBookingSnap = await get(singleBookingRef(booking.id));
  const currentBooking = currentBookingSnap.val();
  if (currentBooking && currentBooking.emptyPickUpContainer) {
    throw new Error("Empty Pick Up Container has already been submitted.");
  }
  const requiredFields = [
    "emptyPickupContainerYardName",
    "pickupDateTime",
    "eirNumber",
    "emptyContainerCondition",
    "lastEmptyPulloutDate"
  ];
  const missingField = requiredFields.find((field) => !pickupData || !pickupData[field]);
  if (missingField || !imageBlob) {
    throw new Error("Please complete all Empty Pick Up Container required fields.");
  }

  const settingsdata = await get(firebase.settingsRef);
  const settings = settingsdata.val();
  const imageType = "empty_pickup_container";
  await uploadBytesResumable(bookingImageRef(booking.id, imageType), imageBlob);
  if (imageBlob.close) {
    imageBlob.close();
  }
  const imageUrl = await getDownloadURL(bookingImageRef(booking.id, imageType));

  const proof = buildProofRecord({
    booking,
    proofType: "empty_pickup_container_photo",
    imageType,
    imageUrl,
    location: pickupData.location,
    target: null,
    settings,
    previousProofHash: getLastProofHash(booking),
    capturedAt: pickupData.proofTimestampIso || pickupData.capturedAt || pickupData.pickupDateTime,
    stampText: pickupData.stampText,
    stampEmbedded: pickupData.stampEmbedded === true
  });
  const now = new Date();
  const payload = {
    bookingId: booking.id,
    driverName: booking.driver_name || "",
    exporterShipperName: pickupData.exporterShipperName || getEmptyPickUpExporterShipperName(booking),
    containerNumbers: getEmptyPickUpContainerNumbers(booking),
    additionalContainerNumbers: (pickupData.additionalContainerNumbers || []).filter((item) => item && item.trim()),
    emptyPickupContainerYardId: pickupData.emptyPickupContainerYardId || "",
    emptyPickupContainerYardName: pickupData.emptyPickupContainerYardName || "",
    pickupDateTime: pickupData.pickupDateTime || "",
    eirNumber: pickupData.eirNumber || "",
    emptyContainerCondition: pickupData.emptyContainerCondition || "",
    lastEmptyPulloutDate: pickupData.lastEmptyPulloutDate || "",
    gpsPictureUrl: imageUrl,
    gpsPictureProof: proof,
    submittedAt: now.toISOString(),
    submittedBy: store.getState().auth && store.getState().auth.profile ? store.getState().auth.profile.uid : null
  };

  await updateScopedBookingPatch(booking.id, {
    emptyPickUpContainer: payload
  });

  const updatedBooking = {
    ...booking,
    emptyPickUpContainer: payload
  };

  dispatch({
    type: UPDATE_BOOKING,
    payload: updatedBooking,
  });

  let emailError = null;
  if (shouldSendWorkflowEmail(updatedBooking, "emptyPickupNotification")) {
    try {
      const res = await fetch(`https://us-central1-${config.projectId}.cloudfunctions.net/send_empty_pickup_container`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bookingId: booking.id })
      });
      const json = await res.json();
      if (!res.ok || json.error) {
        throw new Error(json.error || "Unable to send Empty Pick Up Container email.");
      }
    } catch (error) {
      emailError = error && error.message ? error.message : "Unable to send Empty Pick Up Container email.";
    }
  }

  return { booking: updatedBooking, emptyPickUpContainer: payload, emailError };
};

export const submitEmptyContainerReturn = (booking, returnData, imageBlob) => async (dispatch) => {
  const {
    singleBookingRef,
    bookingOperationsRef,
    bookingImageRef,
    config
  } = firebase;

  if (!booking || !booking.id) {
    throw new Error("Missing booking.");
  }
  if (!canUseEmptyContainerReturnWorkflow(booking)) {
    throw new Error("Empty Container Return is available only for completed Import logistics bookings.");
  }
  validateEmptyContainerReturnSubmission(returnData, imageBlob);

  const settingsdata = await get(firebase.settingsRef);
  const settings = settingsdata.val();
  const returnRef = push(child(singleBookingRef(booking.id), "emptyContainerReturns"));
  const returnId = returnRef.key;
  const imageType = `empty_container_return_${returnId}`;
  let imageUrl = returnData && returnData.arrivalPictureUrl ? returnData.arrivalPictureUrl : "";

  if (imageBlob) {
    await uploadBytesResumable(bookingImageRef(booking.id, imageType), imageBlob);
    if (imageBlob.close) {
      imageBlob.close();
    }
    imageUrl = await getDownloadURL(bookingImageRef(booking.id, imageType));
  }

  const proof = buildProofRecord({
    booking,
    proofType: "empty_container_return_photo",
    imageType,
    imageUrl,
    location: returnData && returnData.location,
    target: null,
    settings,
    previousProofHash: getLastProofHash(booking),
    capturedAt: returnData && (returnData.proofTimestampIso || returnData.capturedAt || returnData.arrivalDateTime),
    stampText: returnData && returnData.stampText,
    stampEmbedded: returnData && returnData.stampEmbedded === true
  });
  const now = new Date();
  const status = returnData.status === "Accepted" ? "Accepted" : "Rejected";
  const resolvedRejectionReason = status === "Rejected"
    ? (returnData.emptyReturnRejectedReason || returnData.rejectionReason || "")
    : "";
  if (status === "Rejected" && !resolvedRejectionReason.trim()) {
    throw new Error("Container Yard Reason of Rejection is required for rejected empty container returns.");
  }
  const uploadReturnFile = async ({ fieldKey, file, allowedTypes = [] }) => {
    if (!file) return null;
    if (file.type && allowedTypes.length && allowedTypes.indexOf(file.type) === -1) {
      throw new Error("Unsupported file type.");
    }
    const fileName = file.name || "";
    const contentType = file.type || "";
    const fileRefKey = `empty_container_return_${returnId}_${fieldKey}`;
    await uploadBytesResumable(bookingImageRef(booking.id, fileRefKey), file);
    if (file.close) {
      file.close();
    }
    const url = await getDownloadURL(bookingImageRef(booking.id, fileRefKey));
    return {
      url,
      imageUrl: url,
      name: fileName,
      contentType,
      uploadedAt: now.toISOString()
    };
  };
  const imageTypes = ["image/jpeg", "image/png"];
  const eirReceiptRecord = await uploadReturnFile({
    fieldKey: "eir_receipt",
    file: returnData.eirReceiptFile,
    allowedTypes: [...imageTypes, "application/pdf"]
  });
  const proofFiles = returnData.proofFiles || {};
  const proofs = {};
  if (eirReceiptRecord && eirReceiptRecord.contentType && eirReceiptRecord.contentType.indexOf("image/") === 0) {
    proofs.eirReceiptPhoto = eirReceiptRecord;
  }
  for (const requirement of EMPTY_CONTAINER_RETURN_UPLOAD_REQUIREMENTS) {
    const fieldKey = requirement.key.replace(/([A-Z])/g, "_$1").toLowerCase();
    const uploadedPhoto = await uploadReturnFile({
      fieldKey,
      file: proofFiles[requirement.key],
      allowedTypes: imageTypes
    });
    if (uploadedPhoto) {
      const metadata = (returnData.proofMetadata || {})[requirement.key] || {};
      proofs[requirement.key] = {
        ...uploadedPhoto,
        ...(metadata.capturedAt ? { capturedAt: metadata.capturedAt } : {}),
        ...(metadata.source ? { source: metadata.source } : {}),
        ...(metadata.lat !== undefined ? { lat: metadata.lat } : {}),
        ...(metadata.lng !== undefined ? { lng: metadata.lng } : {}),
        ...(metadata.accuracy !== undefined ? { accuracy: metadata.accuracy } : {}),
        ...(metadata.stampText ? { stampText: metadata.stampText, stampEmbedded: metadata.stampEmbedded === true } : {})
      };
    }
  }
  const normalizedStatus = status.toLowerCase();
  const arrivalGps = returnData && returnData.emptyReturnArrivalGps
    ? returnData.emptyReturnArrivalGps
    : {
        lat: proof.lat,
        lng: proof.lng,
        accuracy: returnData && returnData.location ? returnData.location.accuracy || null : null,
        capturedAt: returnData && returnData.location ? returnData.location.capturedAt || now.toISOString() : now.toISOString()
      };
  const completionGps = returnData && returnData.emptyReturnCompletionGps
    ? returnData.emptyReturnCompletionGps
    : arrivalGps;
  const arrivedAt = returnData.arrivalDateTime || now.toISOString();
  const completedAt = returnData.emptyReturnCompletedAt || returnData.completedAt || now.toISOString();
  const updatedAt = now.getTime();
  const payload = {
    id: returnId,
    bookingId: booking.id,
    bookingReference: booking.reference || "",
    driverName: returnData.driverName || booking.driver_name || booking.driverName || "",
    customerName: returnData.customerName || booking.customer_name || booking.customerName || "",
    consigneeName: returnData.consigneeName || getEmptyReturnConsigneeName(booking),
    fleetAdminName: returnData.fleetAdminName || booking.fleetadminName || booking.fleetadmin || "",
    containerNumber: returnData.containerNumber || booking.containerNumber || "",
    additionalContainerNumbers: (returnData.additionalContainerNumbers || []).filter((item) => item && item.trim()),
    containerYardId: returnData.containerYardId || "",
    containerYardName: returnData.containerYardName || "",
    arrivalDateTime: arrivedAt,
    arrivalPictureUrl: imageUrl,
    arrivalPictureProof: proof,
    status,
    rejectionReason: resolvedRejectionReason,
    containerYardRemarks: returnData.containerYardRemarks || "",
    acceptanceDate: status === "Accepted" ? (returnData.acceptanceDate || now.toISOString()) : "",
    emptyReturnStatus: normalizedStatus,
    emptyReturnRejectedReason: resolvedRejectionReason,
    emptyReturnRemarks: returnData.containerYardRemarks || "",
    emptyReturnArrivedAt: arrivedAt,
    emptyReturnCompletedAt: completedAt,
    emptyReturnArrivalGps: arrivalGps,
    emptyReturnCompletionGps: completionGps,
    emptyReturnGeofenceChecked: returnData.emptyReturnGeofenceChecked === true,
    emptyReturnGeofencePassed: returnData.emptyReturnGeofencePassed === true
      ? true
      : (returnData.emptyReturnGeofencePassed === false ? false : null),
    emptyReturnGeofenceDistanceMeters: returnData.emptyReturnGeofenceDistanceMeters !== undefined && returnData.emptyReturnGeofenceDistanceMeters !== null
      ? returnData.emptyReturnGeofenceDistanceMeters
      : "",
    emptyReturnGeofenceRadiusMeters: returnData.emptyReturnGeofenceRadiusMeters || "",
    emptyReturnYardLatitude: returnData.emptyReturnYardLatitude !== undefined && returnData.emptyReturnYardLatitude !== null ? returnData.emptyReturnYardLatitude : "",
    emptyReturnYardLongitude: returnData.emptyReturnYardLongitude !== undefined && returnData.emptyReturnYardLongitude !== null ? returnData.emptyReturnYardLongitude : "",
    yardReceiverName: returnData.yardReceiverName || "",
    yardReceiverPosition: returnData.yardReceiverPosition || "",
    emptyReturnEirReceiptUrl: eirReceiptRecord ? eirReceiptRecord.url : (returnData.emptyReturnEirReceiptUrl || ""),
    eirReceiptUrl: eirReceiptRecord ? eirReceiptRecord.url : (returnData.eirReceiptUrl || ""),
    photoRequirementsVersion: EMPTY_CONTAINER_RETURN_PHOTO_REQUIREMENTS_VERSION,
    proofs,
    createdAt: now.getTime(),
    createdBy: store.getState().auth && store.getState().auth.profile ? store.getState().auth.profile.uid : null
  };

  const updateData = {
    [`emptyContainerReturns/${returnId}`]: payload,
    emptyContainerReturnLastId: returnId,
    emptyContainerReturnLastStatus: status,
    updatedAt
  };
  if (status === "Accepted") {
    updateData.emptyContainerReturnClosed = true;
    updateData.emptyContainerReturnAcceptedAt = payload.acceptanceDate;
  } else {
    updateData.emptyContainerReturnClosed = false;
  }

  await update(bookingOperationsRef(booking.id), updateData);

  const updatedBooking = {
    ...booking,
    emptyContainerReturns: {
      ...(booking.emptyContainerReturns || {}),
      [returnId]: payload
    },
    emptyContainerReturnLastId: returnId,
    emptyContainerReturnLastStatus: status,
    emptyContainerReturnClosed: status === "Accepted",
    emptyContainerReturnAcceptedAt: status === "Accepted" ? payload.acceptanceDate : booking.emptyContainerReturnAcceptedAt,
    updatedAt
  };

  dispatch({
    type: UPDATE_BOOKING,
    payload: updatedBooking,
  });

  let emailError = null;
  if (shouldSendWorkflowEmail(updatedBooking, "emptyReturnNotification")) {
    try {
      const res = await fetch(`https://us-central1-${config.projectId}.cloudfunctions.net/send_empty_container_return`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bookingId: booking.id, returnId })
      });
      const json = await res.json();
      if (!res.ok || json.error) {
        throw new Error(json.error || "Unable to send Empty Container Return email.");
      }
    } catch (error) {
      emailError = error && error.message ? error.message : "Unable to send Empty Container Return email.";
    }
  }

  return { booking: updatedBooking, attempt: payload, emailError };
};

export const updateBookingImage = (booking, imageType, imageBlob, proofMeta) => async (dispatch) => {
  const   {
    singleBookingRef,
    bookingImageRef
  } = firebase;
  const settingsdata = await get(firebase.settingsRef);
  const settings = settingsdata.val();
  return uploadBytesResumable(bookingImageRef(booking.id,imageType), imageBlob).then(() => {
    imageBlob.close()
    return getDownloadURL(bookingImageRef(booking.id,imageType))
  }).then((url) => {
    if(imageType == 'pickup_image'){
      booking.pickup_image = url;
      booking.pickup_image_proof = buildProofRecord({
        booking,
        proofType: "photo",
        imageType,
        imageUrl: url,
        location: proofMeta && proofMeta.location,
        target: booking.pickup,
        settings,
        previousProofHash: getLastProofHash(booking),
        capturedAt: proofMeta && proofMeta.capturedAt,
        stampText: proofMeta && proofMeta.stampText,
        stampEmbedded: proofMeta && proofMeta.stampEmbedded === true
      });
      booking.pickupImageTimestamp = booking.pickup_image_proof.timestampIso;
      booking.pickupImageLat = booking.pickup_image_proof.lat;
      booking.pickupImageLng = booking.pickup_image_proof.lng;
      booking.pickupImageProofHash = booking.pickup_image_proof.proofHash;
    }
    if(imageType == 'deliver_image'){
      booking.deliver_image = url;
      booking.deliver_image_proof = buildProofRecord({
        booking,
        proofType: "photo",
        imageType,
        imageUrl: url,
        location: proofMeta && proofMeta.location,
        target: booking.drop,
        settings,
        previousProofHash: getLastProofHash(booking),
        capturedAt: proofMeta && proofMeta.capturedAt,
        stampText: proofMeta && proofMeta.stampText,
        stampEmbedded: proofMeta && proofMeta.stampEmbedded === true
      });
      booking.deliverImageTimestamp = booking.deliver_image_proof.timestampIso;
      booking.deliverImageLat = booking.deliver_image_proof.lat;
      booking.deliverImageLng = booking.deliver_image_proof.lng;
      booking.deliverImageProofHash = booking.deliver_image_proof.proofHash;
    }
    if(imageType == 'container_yard_image'){
      booking.container_yard_image = url;
      booking.container_yard_image_proof = buildProofRecord({
        booking,
        proofType: "photo",
        imageType,
        imageUrl: url,
        location: proofMeta && proofMeta.location,
        target: booking.drop,
        settings,
        previousProofHash: getLastProofHash(booking),
        capturedAt: proofMeta && proofMeta.capturedAt,
        stampText: proofMeta && proofMeta.stampText,
        stampEmbedded: proofMeta && proofMeta.stampEmbedded === true
      });
      booking.containerYardImageTimestamp = booking.container_yard_image_proof.timestampIso;
      booking.containerYardImageLat = booking.container_yard_image_proof.lat;
      booking.containerYardImageLng = booking.container_yard_image_proof.lng;
      booking.containerYardImageProofHash = booking.container_yard_image_proof.proofHash;
    }
    if(imageType == 'container_yard_video'){
      booking.container_yard_video = url;
      booking.container_yard_video_proof = buildProofRecord({
        booking,
        proofType: "video",
        imageType,
        imageUrl: url,
        location: proofMeta && proofMeta.location,
        target: booking.drop,
        settings,
        previousProofHash: getLastProofHash(booking),
        capturedAt: proofMeta && proofMeta.capturedAt,
        stampText: proofMeta && proofMeta.stampText
      });
      booking.containerYardVideoTimestamp = booking.container_yard_video_proof.timestampIso;
      booking.containerYardVideoLat = booking.container_yard_video_proof.lat;
      booking.containerYardVideoLng = booking.container_yard_video_proof.lng;
      booking.containerYardVideoProofHash = booking.container_yard_video_proof.proofHash;
    }
    return updateScopedBookingPatch(booking.id, booking).then(() => booking);
  }).then((updatedBooking) => {
    dispatch({
      type: UPDATE_BOOKING,
      payload: updatedBooking,
    });
    return updatedBooking;
  })
};

export const updateDriverSwornStatement = (booking, statementData) => async (dispatch) => {
  const {
    singleBookingRef
  } = firebase;

  const now = new Date();
  const location = normalizeProofLocation(statementData && statementData.location);
  const driverSwornStatement = {
    statement: statementData.statement,
    signature: statementData.signature,
    rejectionReason: statementData.rejectionReason || booking.yardGateRejectionReason || "",
    signedAt: now.getTime(),
    signedAtIso: now.toISOString(),
    lat: location.lat,
    lng: location.lng,
    driver: booking.driver || null,
    driverName: booking.driver_name || null
  };

  const updateData = {
    yardGateStatus: "Rejected",
    yardGateRejectionReason: driverSwornStatement.rejectionReason,
    driverSwornStatement,
    driverSwornStatementHash: createProofHash({
      bookingId: booking.id,
      ...driverSwornStatement
    })
  };

  return updateScopedBookingPatch(booking.id, updateData, { includeIndexes: true }).then(() => updateData).then((savedData) => {
  dispatch({
    type: UPDATE_BOOKING,
    payload: {
      ...booking,
      ...savedData
    },
  });
    return savedData;
  });
};

export const forceEndBooking = (booking) => async (dispatch) => {

  const {
    trackingRef,
    singleBookingRef,
    singleUserRef,
    walletHistoryRef,
    settingsRef,
  } = firebase;

  dispatch({
    type: UPDATE_BOOKING,
    payload: booking,
  });
  
  if (booking.status == 'STARTED') {

    push(trackingRef(booking.id),{
      at: new Date().getTime(),
      status: 'REACHED',
      lat: booking.drop.lat,
      lng: booking.drop.lng
    });

    const end_time = new Date();
    const diff = (end_time.getTime() - parseFloat(booking.startTime)) / 1000;
    const totalTimeTaken = Math.abs(Math.round(diff));
    booking.trip_end_time = end_time.getHours() + ":" + end_time.getMinutes() + ":" + end_time.getSeconds();
    booking.endTime = end_time.getTime();
    booking.total_trip_time = totalTimeTaken;

    if(booking.customer_token){
      RequestPushMsg(
        booking.customer_token,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title,
            msg: store.getState().languagedata.defaultLanguage.driver_completed_ride,
            screen: 'BookedCab',
            params: { bookingId: booking.id }
        });
    }

    update(singleUserRef(booking.driver),{ queue: false });

    if(booking.prepaid){

      const settingsdata = await get(settingsRef);
      const settings = settingsdata.val();

      onValue(singleUserRef(booking.driver), snapshot => {
        let walletBalance = parseFloat(snapshot.val().walletBalance);
        walletBalance = walletBalance + parseFloat(booking.driver_share);
        if(parseFloat(booking.cashPaymentAmount)>0){
          walletBalance = walletBalance - parseFloat(booking.cashPaymentAmount);
        }
        update(singleUserRef(booking.driver),{"walletBalance": parseFloat(walletBalance.toFixed(settings.decimal))});

        let details = {
          type: 'Credit',
          amount: parseFloat(booking.driver_share).toFixed(settings.decimal),
          date: new Date().getTime(),
          txRef: booking.id
        }
        push(walletHistoryRef(booking.driver),details);
        
        if(parseFloat(booking.cashPaymentAmount)>0){
          let details = {
            type: 'Debit',
            amount: booking.cashPaymentAmount,
            date: new Date().getTime(),
            txRef: booking.id
          }
          push(walletHistoryRef(booking.driver), details);
        }  
      },{onlyOnce: true});

      if(booking.customer_token){
        RequestPushMsg(
          booking.customer_token,
          {
              title: store.getState().languagedata.defaultLanguage.notification_title,
              msg: store.getState().languagedata.defaultLanguage.success_payment,
              screen: 'BookedCab',
              params: { bookingId: booking.id }
          });
      }

      if(booking.driver_token){
        RequestPushMsg(
          booking.driver_token,
          {
              title: store.getState().languagedata.defaultLanguage.notification_title,
              msg: store.getState().languagedata.defaultLanguage.success_payment,
              screen: 'BookedCab',
              params: { bookingId: booking.id }
          });
        }
      booking.status = 'PAID';
    } else{
      booking.status = 'PENDING';
    }

    updateScopedBookingPatch(booking.id, booking);
  }
};
